import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import DOMPurify from "dompurify"
import { getEmailInsights } from "services/stat"
import { verifyResponse } from "utils/httpRequest"
import EventEmailCadence from "components/common/EventEmailCadence"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

const Wrap = styled.div`
  display: grid;
  gap: 18px;
`

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 12px;
`

const Metric = styled.div`
  padding: 18px;
  border: 1px solid var(--cms-border);
  border-radius: 12px;
  background: var(--cms-card-bg);
  .label { color: var(--cms-text-secondary); font-size: 12px; text-transform: uppercase; }
  .value { color: var(--cms-text-primary); font-size: 28px; font-weight: 700; margin-top: 4px; }
`

const Panel = styled.div`
  padding: 18px;
  border: 1px solid var(--cms-border);
  border-radius: 12px;
  background: var(--cms-card-bg);
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const STATUS_COLORS = {
  clicked: "success",
  opened: "success",
  delivered: "info",
  submitted: "default",
  bounced: "warning",
  failed: "error",
}

const STATUS_HELP = {
  submitted: "HubSpot accepted the send request, but delivery has not yet been confirmed.",
  delivered: "The recipient's mail server accepted the email.",
  opened: "HubSpot recorded at least one open.",
  clicked: "HubSpot recorded at least one link click.",
  bounced: "The recipient's mail server rejected the email.",
  failed: "The email could not be submitted or delivered.",
}

const SOURCE_LABELS = {
  agent_scheduled_reminder: "Agent reminders",
  event_registered_email: "Registered event reminders",
  event_audience_email: "Event audience series",
  event_new_member_email: "New member event invites",
  event_registration_confirmation: "Event confirmations",
  activity_inactivity_reminder: "Activity reminders",
  periodic_report: "Periodic reports",
  practitioner_invite: "Practitioner invites",
  practitioner_linked: "Practitioner linked",
  password_reset: "Password resets",
  hubspot_workflow: "HubSpot workflows",
  direct_hubspot: "Other backend emails",
}

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "—"

const EmailsInsights = ({ dateRange }) => {
  const [loading, setLoading] = useState(false)
  const [insights, setInsights] = useState(null)
  const [error, setError] = useState("")
  const [page, setPage] = useState(1)
  const [sourceType, setSourceType] = useState("")
  const [deliveryStatus, setDeliveryStatus] = useState("")
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState("desc")
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const requestSequence = useRef(0)
  const startAt = dateRange?.[0]?.valueOf()
  const endAt = dateRange?.[1]?.valueOf()

  const load = useCallback(async () => {
    if (!startAt || !endAt) return
    const requestId = ++requestSequence.current
    setLoading(true)
    setError("")
    try {
      const response = await getEmailInsights({
        startAt,
        endAt,
        page,
        pageSize: 25,
        sourceType,
        deliveryStatus,
        search: search.trim(),
        sortBy,
        sortOrder: sortOrder.toUpperCase(),
      })
      verifyResponse(response)
      if (requestId === requestSequence.current) {
        setInsights(response.data)
      }
    } catch (requestError) {
      if (requestId === requestSequence.current) {
        setError(
          requestError?.response?.data?.message ||
            "Email insights could not be loaded. Confirm your role has email insights permission."
        )
      }
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false)
      }
    }
  }, [startAt, endAt, page, sourceType, deliveryStatus, search, sortBy, sortOrder])

  useEffect(() => {
    setPage(1)
  }, [startAt, endAt])

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  const openDetail = async (id) => {
    setDetailLoading(true)
    try {
      const response = await getEmailInsights({ emailSendId: id })
      verifyResponse(response)
      setDetail(response.data)
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Email detail could not be loaded.")
    } finally {
      setDetailLoading(false)
    }
  }

  const summary = insights?.summary || {}
  const activeRangeLabel =
    startAt && endAt
      ? `${new Date(startAt).toLocaleDateString()} – ${new Date(endAt).toLocaleDateString()}`
      : "Select a date range"
  const pages = Math.max(
    1,
    Math.ceil((insights?.pagination?.total || 0) / (insights?.pagination?.pageSize || 25))
  )
  const handleSort = (column) => {
    setPage(1)
    if (sortBy === column) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"))
      return
    }
    setSortBy(column)
    setSortOrder(column === "createdAt" ? "desc" : "asc")
  }

  return (
    <Wrap>
      {insights?.partialHistory && (
        <Alert severity="info">{insights.partialHistoryNote}</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <EventEmailCadence />
      <Metrics>
        {[
          ["Sent", summary.total || 0],
          ["Delivered", summary.delivered || 0],
          ["Unique open rate", `${summary.openRate || 0}%`],
          ["Unique click rate", `${summary.clickRate || 0}%`],
          ["Bounced", summary.bounced || 0],
          ["Failed", summary.failed || 0],
        ].map(([label, value]) => (
          <Metric key={label}>
            <div className="label">{label}</div>
            <div className="value">{value}</div>
          </Metric>
        ))}
      </Metrics>

      <Panel>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={1.5}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Email activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing sends created during {activeRangeLabel}
            </Typography>
          </Box>
          {loading && <CircularProgress size={20} />}
        </Stack>
        {loading && insights && <LinearProgress sx={{ mb: 1.5 }} />}
        <Filters>
          <TextField
            size="small"
            label="Search recipient or subject"
            value={search}
            onChange={(event) => {
              setPage(1)
              setSearch(event.target.value)
            }}
            sx={{ minWidth: 270 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={sourceType}
              onChange={(event) => {
                setPage(1)
                setSourceType(event.target.value)
              }}
            >
              <MenuItem value="">All sources</MenuItem>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={deliveryStatus}
              onChange={(event) => {
                setPage(1)
                setDeliveryStatus(event.target.value)
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {["submitted", "delivered", "opened", "clicked", "bounced", "failed"].map(
                (status) => <MenuItem key={status} value={status}>{status}</MenuItem>
              )}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
        </Filters>

        {loading && !insights ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      ["createdAt", "Sent"],
                      ["recipientEmail", "Recipient"],
                      ["subject", "Subject"],
                      ["sourceType", "Source"],
                      ["deliveryStatus", "Status"],
                      ["openCount", "Opens"],
                      ["clickCount", "Clicks"],
                    ].map(([column, label]) => (
                      <TableCell
                        key={column}
                        sortDirection={sortBy === column ? sortOrder : false}
                      >
                        <TableSortLabel
                          active={sortBy === column}
                          direction={sortBy === column ? sortOrder : "asc"}
                          onClick={() => handleSort(column)}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(insights?.rows || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>
                        <div>{row.recipientName || "—"}</div>
                        <div style={{ color: "var(--cms-text-secondary)", fontSize: 12 }}>
                          {row.recipientEmail}
                        </div>
                      </TableCell>
                      <TableCell>{row.subject || "—"}</TableCell>
                      <TableCell>{SOURCE_LABELS[row.sourceType] || row.sourceType}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={STATUS_HELP[row.deliveryStatus] || "Latest HubSpot delivery state."}
                          arrow
                        >
                          <Chip
                            size="small"
                            label={row.deliveryStatus}
                            color={STATUS_COLORS[row.deliveryStatus] || "default"}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.openCount || 0}</TableCell>
                      <TableCell>{row.clickCount || 0}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => openDetail(row.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!insights?.rows?.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No tracked emails in this range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Pagination
                count={pages}
                page={page}
                onChange={(_, value) => setPage(value)}
              />
            </Box>
          </>
        )}
      </Panel>

      <Dialog open={Boolean(detail) || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Email recipient activity</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
          ) : detail && (
            <Box display="grid" gap={2}>
              <div><strong>Recipient:</strong> {detail.send.recipientEmail}</div>
              <div><strong>Subject:</strong> {detail.send.subject || "—"}</div>
              <div><strong>Status:</strong> {detail.send.deliveryStatus}</div>
              {detail.send.hubspotUrl && (
                <div>
                  <Button
                    component="a"
                    href={detail.send.hubspotUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                  >
                    {detail.send.hubspotLinkIsDirect
                      ? "Open email in HubSpot"
                      : "Open HubSpot email tool"}
                  </Button>
                </div>
              )}
              <div>
                <strong>Activity:</strong>
                {(detail.engagements || []).length ? (
                  <ul>
                    {detail.engagements.map((event) => (
                      <li key={event.id}>
                        {event.eventType} · {formatDate(event.occurredAt)}
                        {event.clickedUrl ? (
                          <> · <a href={event.clickedUrl} target="_blank" rel="noreferrer">{event.clickedUrl}</a></>
                        ) : null}
                        {event.metadata?.response ? (
                          <> · {event.metadata.response}</>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : " No HubSpot activity recorded yet."}
              </div>
              <div><strong>Preview</strong></div>
              {detail.send.isRedacted ? (
                <Alert severity="warning">This email contains a sensitive link, so its body is not stored.</Alert>
              ) : detail.send.bodyHtml ? (
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={DOMPurify.sanitize(detail.send.bodyHtml)}
                  style={{
                    width: "100%",
                    minHeight: 360,
                    border: "1px solid var(--cms-border)",
                  }}
                />
              ) : (
                <Alert severity="info">A preview is not available for this HubSpot workflow email.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Wrap>
  )
}

export default EmailsInsights
